import _ from "underscore";
import { api } from "./../../shared/api/system/apiClient";
import { FileChunkTracker } from "./chunks/fileChunk";
import { IFileChunk, IUploaderConfig, IUploadMetadata } from "./config";
import { ITask, ITaskQueue, ITaskResult, TaskResultFactory } from "./tasks/task";
import { SequentialTaskQueueProcessor, TaskProcessingResult, TaskQueueStatus } from "./tasks/taskProcessor";

export interface IFileUploadManager {
  fileName: string;
  thumbnailUrl: string;
  bytesRemaining: number;
  bytesUploaded: number;
  bytesTotal: number;
  message: string;
  getStatus(): TaskQueueStatus;
  getAudits(): TaskProcessingResult[];
  retry(): void;
  cancel(): void;
  remove(): void;
}

enum UploadItemState {
  Pending,
  Initialized,
  ChunksComplete,
  Finalized,
}

class FileUploadManager implements IFileUploadManager {
  constructor(private tracker: FileChunkTracker, private taskProvider: UploadTaskProvider, private processer: SequentialTaskQueueProcessor, private notifyChange: () => void) {
  }

  public thumbnailUrl: string;
  public message: string;

  get fileName() {
    return this.tracker.fileName;
  }

  get bytesTotal(): number {
    return this.tracker.fileSize;
  }

  get bytesRemaining(): number {
    return this.tracker.bytesRemaining;
  }

  get bytesUploaded(): number {
    return this.tracker.bytesUploaded;
  }

  public getStatus() {
    return this.processer.getStatus(this.taskProvider);
  }

  public getAudits() {
    return this.processer.getAudits(this.taskProvider);
  }

  public remove() {
    this.processer.remove(this.taskProvider);
    this.notifyChange();
  }

  public retry() {
    this.processer.enqueue(this.taskProvider);
    this.notifyChange();
  }

  public cancel() {
    this.processer.cancel(this.taskProvider);
    this.notifyChange();
  }
}

// tslint:disable-next-line: max-classes-per-file
export class UploadTaskProvider implements ITaskQueue {
  private tracker: FileChunkTracker;
  public name: string;
  public upload: IFileUploadManager;

  constructor(
    private file: File,
    private type: string,
    private subtype: string,
    private additionalMetadata: any,
    private config: IUploaderConfig,
    processer: SequentialTaskQueueProcessor,
    private notifyChange: () => void,
    private onComplete: (result: IUploadJob) => void) {

    this.name = file.name;
    this.tracker = new FileChunkTracker(file, config.chunkSizeProvider, notifyChange)
    this.upload = new FileUploadManager(this.tracker, this, processer, notifyChange);

    // Only generate thumbnails for relatively small images...
    if (file.size < 1000000) {
      const objectURL = window.URL.createObjectURL(file);
      this.upload.thumbnailUrl = objectURL;
    }
    else {
      this.upload.thumbnailUrl = "/images/missing-image.jpg";
    }
  }

  private state = UploadItemState.Pending;
  private ticket: string;
  private uploadedFilename: string;

  public next(): ITask {
    switch (this.state) {
      case UploadItemState.Pending:
        return this.getTicketTask();
      case UploadItemState.Initialized:
        return this.getChunkUploadTask();
      case UploadItemState.ChunksComplete:
        return this.getFinalizerTask();
      case UploadItemState.Finalized:
        this.notifyChange();
        return null;
    }
  }

  private getChunkUploadTask(): ITask {
    const chunk = this.tracker.getNextChunk();
    if (chunk) {
      return this.sendChunkTask(chunk);
    }
    this.state = UploadItemState.ChunksComplete;
    this.notifyChange()
    return this.getFinalizerTask();
  }

  private getFinalizerTask() {
    return {
      name: "Custom Finalizer",
      run: async () => {
        const result = await this.pollStatus();
        return result;
      }
    };
  }

  private delayPromise() {
    return new Promise<ITaskResult>(res => {
      window.setTimeout(() => {
        res(TaskResultFactory.ok("processing"))
      }, 1000);
    });
  }

  private pollStatus(): Promise<ITaskResult> {
    return api.pollUploadStatus(this.uploadedFilename).then((response) => {
      const status = response.json.status;
      if (status === UploadJobStatus.Done) {
        this.state = UploadItemState.Finalized;
        this.notifyChange();

        // notifyChange gets called from so many places I'm making an executive decision to ignore it
        this.onComplete(response.json);

        return TaskResultFactory.ok();
      }
      return this.delayPromise();
    });
  }

  private getTicketTask() {
    return {
      name: "Get ticket", run: () => this.config.ticketProvider.getTicket(this.getUploadMetadata(this.file, this.type, this.subtype, this.additionalMetadata)).then(t => {
        if (!t) {
          return TaskResultFactory.error("Unable to get upload ticket");
        }
        this.ticket = t;
        this.state = UploadItemState.Initialized;
        this.notifyChange();
        return TaskResultFactory.ok();
      })
    };
  }

  private getUploadMetadata(file: File, type: string, subtype: string, additionalMetadata: any): IUploadMetadata {
    return { type, subtype, name: file.name, size: file.size, contentType: file.type, additionalMetadata };
  }

  private sendChunkTask(chunk: IFileChunk) {
    return { name: "Send Chunk", run: () => this.sendChunk(chunk) };
  }

  private sendChunk(chunk: IFileChunk) {
    return this.config.chunkUploader.upload(this.ticket, chunk).then(fileName => {
      if (!fileName) {
        this.tracker.notifyUploaded(chunk);
        this.notifyChange();
        return TaskResultFactory.ok()
      } else {
        this.tracker.notifyUploaded(chunk);
        this.uploadedFilename = fileName;
        this.notifyChange();
        return TaskResultFactory.ok()
      }
    }, err => {
      this.tracker.notifyFailed(chunk.info);
      this.notifyChange()
      return TaskResultFactory.error("Upload chunk failed: " + err.toString());
    });
  }
}
